/*
  LH filter drawer (2026-08-22, staged-AJAX model)
  Companion to snippets/lh-filter-groups.liquid. Loads only when the section
  setting "Use the LH filter drawer" is on, and never edits facets.js.

  WHY IT IS SHAPED THIS WAY
  The stock canvas drawer auto-applied on a 500ms debounce off the form's
  `input` event, and its Apply button only closed the drawer. That produced a
  string of races, every one of them reported as "nothing filters": Apply
  closing over an unrefreshed grid, the debounce landing after Cancel (so
  Cancel applied), and the debounce firing against a checkbox renderFilters had
  already replaced (an uncaught TypeError).

  So: ticks STAGE, they no longer submit - that alone removes every race and
  cuts a multi-box selection from N requests to one. Apply then calls the
  theme's own in-place renderer, so the grid swaps without a page reload. To
  make that swap feel instant, a tick quietly warms the theme's section cache
  in the background, and Apply usually renders with no network wait at all.

  Jobs:
    1. price pills  -> write the hidden gte/lte inputs (staged, no request)
    2. tag aliases  -> keep the hidden alias checkboxes in step with the
                       visible one, so "For Mom" submits mom AND mama
                       (Search & Discovery tag logic must be set to OR)
    3. no auto-submit -> neutralise the theme's debounced submit for this form
    4. Apply        -> prefetch on tick, then render in place from cache
    5. Clear all    -> take the anchor's href ourselves and render in place
    6. Cancel       -> close, and put the controls back how they were on open
    7. cosmetics    -> hide empty groups, keep the Filters badge in step
*/
(function () {
  'use strict';

  const FORM_ID = 'FacetFiltersForm';
  const form = () => document.getElementById(FORM_ID);

  /* ---------------------------------------------------------------- 1. price */
  class LhPricePills extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const pill = event.target.closest('.lh-pill');
      if (!pill) return;
      event.preventDefault();

      const gte = pill.dataset.gte || '';
      const lte = pill.dataset.lte || '';
      const gteInput = this.querySelector('.lh-price-gte');
      const lteInput = this.querySelector('.lh-price-lte');
      if (!gteInput || !lteInput) return;

      // A disabled input is skipped by FormData, which is how an empty bound
      // stays out of the URL instead of leaving "filter.v.price.gte=" behind.
      gteInput.value = gte;
      gteInput.disabled = gte === '';
      lteInput.value = lte;
      lteInput.disabled = lte === '';

      this.querySelectorAll('.lh-pill').forEach((el) => el.classList.toggle('is-active', el === pill));
      refresh();
      schedulePrefetch();
    }
  }
  if (!customElements.get('lh-price-pills')) customElements.define('lh-price-pills', LhPricePills);

  /* -------------------------------------------------------------- 2. aliases */
  document.addEventListener(
    'change',
    function (event) {
      const input = event.target;
      if (!input || !input.matches || !input.matches('.lh-facet-checkbox input[data-lh-aliases]')) return;
      const item = input.closest('.facets__item');
      if (!item) return;
      item.querySelectorAll('.lh-alias').forEach((alias) => {
        alias.checked = input.checked;
        alias.disabled = !input.checked;
      });
      refresh();
      schedulePrefetch();
    },
    true
  );

  /* ------------------------------------------------------- 3. no auto-submit */
  /*
    facets.js binds a debounced onSubmitHandler to this form's `input` event in
    its constructor, which we cannot unbind. Neutralise it at the method
    instead, and only for this form - the sort dropdown is a separate form
    (FacetSortForm) that must keep working exactly as the theme intends.
  */
  (function stopAutoSubmit() {
    if (typeof FacetFiltersForm === 'undefined') return;
    const proto = FacetFiltersForm.prototype;
    if (!proto || typeof proto.onSubmitHandler !== 'function' || proto.__lhStaged) return;
    const original = proto.onSubmitHandler;
    proto.onSubmitHandler = function (event) {
      const target = event && event.target;
      if (!target || typeof target.closest !== 'function') return;
      const owner = target.closest('form');
      // No owning form means the target has been DETACHED - a tick's debounce
      // outliving the re-render that replaced its checkbox. Testing "is it
      // inside the drawer" is not enough on its own: a detached node answers
      // no to that too, and then fell through to the original, which does
      // event.target.closest('form').id on a null and throws.
      if (!owner) return;
      // The drawer stages; only Apply and Clear all submit it.
      if (owner.id === FORM_ID) return;
      return original.call(this, event);
    };
    proto.__lhStaged = true;
  })();

  /* ---------------------------------------------------------------- 4. apply */
  /*
    Build the URL from the form itself rather than from the theme's
    createSearchParams, so exactly what is ticked is what travels. FormData
    already skips disabled inputs (empty price bounds, unchecked aliases) and
    the nameless price trigger.
  */
  function stagedParams() {
    const el = form();
    if (!el) return null;
    const params = new URLSearchParams();
    new FormData(el).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    // Sort lives in its own form; carry the shopper's choice across.
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.append('sort_by', sort);
    return params.toString();
  }

  /*
    The theme caches rendered sections in FacetFiltersForm.filterData, keyed by
    the exact section URL, and renderPage serves from that cache before it will
    fetch. So warm the cache the moment a box is ticked: by the time Apply is
    pressed the HTML is usually already in memory and the swap is instant, with
    no request on the critical path. A tick that is changed again just leaves an
    unused entry behind, which costs nothing.
  */
  function sectionUrl(params) {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) return null;
    return window.location.pathname + '?section_id=' + grid.dataset.id + '&' + params;
  }

  var prefetchTimer = null;

  function prefetchStaged() {
    if (typeof FacetFiltersForm === 'undefined') return;
    const params = stagedParams();
    if (params === null) return;
    const url = sectionUrl(params);
    if (!url) return;
    const cached = (element) => element.url === url;
    if (FacetFiltersForm.filterData.some(cached)) return;
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        if (!FacetFiltersForm.filterData.some(cached)) {
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        }
      })
      .catch(function () {
        /* a failed warm-up just means Apply fetches normally */
      });
  }

  function schedulePrefetch() {
    clearTimeout(prefetchTimer);
    // Short enough to be ready before a shopper can reach Apply, long enough
    // that ticking three boxes quickly does not fire three warm-ups.
    prefetchTimer = setTimeout(prefetchStaged, 150);
  }

  function applyNow() {
    const params = stagedParams();
    if (params === null || typeof FacetFiltersForm === 'undefined') return;
    clearTimeout(prefetchTimer);
    // The theme's own renderer: swaps the grid in place, updates the counts and
    // the group markup, and pushes the URL. One call, fired straight from the
    // button - no debounce to race with, because ticks no longer submit.
    FacetFiltersForm.renderPage(params);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest || !event.target.closest('facet-apply-canvas')) return;
    applyNow();
  });

  document.addEventListener('keypress', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.closest || !event.target.closest('facet-apply-canvas')) return;
    applyNow();
  });

  /* ------------------------------------------------------------ 5. clear all */
  /*
    The button is already <facet-remove><a href="{{ results_url }}">, but
    FacetRemove's constructor binds a handler to that anchor which
    preventDefaults and hands off to the AJAX path. Stopping the event during
    capture means the anchor's own listener never runs, so the browser just
    follows the href - the unfiltered collection.
  */
  /* Untick everything and put the price back to "Any price". */
  function resetControls() {
    const el = form();
    if (!el) return;
    el.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
      // Aliases ride along with their visible checkbox: unchecked means
      // disabled, so FormData leaves them out of the next staged URL.
      if (input.classList.contains('lh-alias')) input.disabled = true;
    });
    ['.lh-price-gte', '.lh-price-lte'].forEach((selector) => {
      const bound = el.querySelector(selector);
      if (!bound) return;
      bound.value = '';
      bound.disabled = true;
    });
    // The first pill is "Any price" - it is the one that means no bound at all.
    el.querySelectorAll('lh-price-pills').forEach((group) => {
      const pills = group.querySelectorAll('.lh-pill');
      pills.forEach((pill, index) => pill.classList.toggle('is-active', index === 0));
    });
    refresh();
  }

  document.addEventListener(
    'click',
    function (event) {
      const link = event.target.closest && event.target.closest('.lh-clear-all a[href]');
      if (!link) return;
      // Capture + stopPropagation: FacetRemove's handler is bound to this very
      // anchor, and it hands off to a path that reads event.currentTarget.href
      // on the wrong node. Take the href ourselves and render in place.
      event.stopPropagation();
      event.preventDefault();
      // Clear the controls SYNCHRONOUSLY. renderPage would eventually replace
      // the group markup with a fresh unticked copy anyway, but that is a fetch
      // plus a re-render away - about a second during which every box is still
      // ticked and the badge still shows a count, which reads as "Clear all did
      // nothing". The re-render below then agrees with what is already on screen.
      resetControls();
      const href = link.getAttribute('href') || window.location.pathname;
      const query = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.renderPage(query);
    },
    true
  );

  /* --------------------------------------------------------------- 6. cancel */
  /*
    Nothing has been applied while the drawer was open, so Cancel only has to
    put the controls back to the state they were in on open - no request, no
    URL change.
  */
  var snapshot = null;

  function takeSnapshot() {
    const el = form();
    if (!el) {
      snapshot = null;
      return;
    }
    snapshot = {
      inputs: Array.from(el.querySelectorAll('input')).map((input) => ({
        input: input,
        checked: input.checked,
        disabled: input.disabled,
        value: input.value,
      })),
      pills: Array.from(el.querySelectorAll('.lh-pill')).map((pill) => ({
        pill: pill,
        active: pill.classList.contains('is-active'),
      })),
    };
  }

  function restoreSnapshot() {
    if (!snapshot) return;
    snapshot.inputs.forEach((row) => {
      row.input.checked = row.checked;
      row.input.disabled = row.disabled;
      row.input.value = row.value;
    });
    snapshot.pills.forEach((row) => row.pill.classList.toggle('is-active', row.active));
  }

  /* Instant overlay for the Filters drawer. The flag goes on in the capture
     phase, before the theme's toggle-nav opens the canvas, and stays on after
     the drawer closes so a second and third open are just as fast. It only
     comes off when another drawer (cart, menu) opens, so those keep their fade. */
  const root = document.documentElement;
  ['pointerdown', 'click'].forEach((type) => {
    document.addEventListener(type, function (event) {
      if (event.target.closest("[data-item='overlay-filter']")) root.classList.add('lh-filter-fast');
    }, true);
  });
  document.addEventListener('click', function (event) {
    if (event.target.closest("[data-item='overlay-filter']")) takeSnapshot();
  });
  new MutationObserver(function () {
    if (
      root.classList.contains('lh-filter-fast') &&
      root.classList.contains('open-canvas') &&
      !document.querySelector('.overlay-filter .bls__canvas.active')
    ) {
      root.classList.remove('lh-filter-fast');
    }
  }).observe(root, { attributes: true, attributeFilter: ['class'] });

  /* The theme closes drawers by removing details[open] 300 ms later (1.3 s
     after Remove). Reopen inside that window and the stale timer shut the
     drawer under the shopper. Put open straight back while the canvas is active. */
  new MutationObserver(function (records) {
    records.forEach((r) => {
      const d = r.target;
      if (d.tagName === 'DETAILS' && d.classList.contains('overlay-filter') && !d.hasAttribute('open') &&
          d.querySelector('.bls__canvas.active')) {
        d.setAttribute('open', '');
      }
    });
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });

  function closeDrawer(el) {
    const canvas = el.closest('.bls__canvas');
    if (!canvas) return;
    if (typeof rootAction !== 'undefined' && rootAction.remove) rootAction.remove();
    canvas.classList.remove('active');
    canvas.removeAttribute('open');
    const details = canvas.closest('details');
    if (details) details.removeAttribute('open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(document.querySelector("[data-item='overlay-filter']"));
    }
  }

  class LhFilterCancel extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onCancel.bind(this));
      this.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') this.onCancel(event);
      });
    }

    onCancel(event) {
      event.preventDefault();
      restoreSnapshot();
      refresh();
      closeDrawer(this);
    }
  }
  if (!customElements.get('lh-filter-cancel')) customElements.define('lh-filter-cancel', LhFilterCancel);

  /* ----------------------------------------------------------- 7. cosmetics */
  /*
    Kept from the AJAX build: the sort dropdown still re-renders the page
    through facets.js, and renderFilters there walks the fresh HTML's groups
    and assumes each one exists in the page. A missing one must be a no-op
    rather than a thrown error.
  */
  if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderFilters) {
    FacetFiltersForm.renderFilters = function (html, event) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );
      const inUse = event && event.target.closest ? event.target.closest('.js-filter') : null;
      const matchesIndex = (element) => (inUse ? element.dataset.index === inUse.dataset.index : false);

      Array.from(fresh)
        .filter((element) => !matchesIndex(element))
        .forEach((element) => {
          const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
          if (target) target.innerHTML = element.innerHTML;
        });

      FacetFiltersForm.renderActiveFacets(parsed);

      const counts = Array.from(fresh).find(matchesIndex);
      if (counts && inUse) FacetFiltersForm.renderCounts(counts, inUse);
      if (typeof actionFilter === 'function') actionFilter();
      refresh();
    };
  }

  /* A group whose every value dropped to zero keeps its heading otherwise. */
  function hideEmptyGroups() {
    document.querySelectorAll('.lh-filter-group').forEach((group) => {
      if (group.classList.contains('lh-filter-group--price')) return;
      const items = group.querySelectorAll('.facets__item');
      if (!items.length) return;
      const visible = Array.from(items).some((item) => !item.querySelector('.facet-checkbox--disabled'));
      group.classList.toggle('lh-filter-group--empty', !visible);
    });
  }

  /* Active-filter count on the Filters button - now counts what is STAGED. */
  function syncFilterCount() {
    const badge = document.querySelector('.lh-filter-count-badge');
    const el = form();
    if (!badge || !el) return;
    let n = el.querySelectorAll('.lh-facet-checkbox input:checked').length;
    const gte = el.querySelector('.lh-price-gte');
    const lte = el.querySelector('.lh-price-lte');
    if ((gte && !gte.disabled && gte.value) || (lte && !lte.disabled && lte.value)) n += 1;
    badge.textContent = n ? String(n) : '';
    badge.classList.toggle('is-on', n > 0);
  }

  function refresh() {
    hideEmptyGroups();
    syncFilterCount();
  }

  document.addEventListener('DOMContentLoaded', refresh);
  if (document.readyState !== 'loading') refresh();
  window.addEventListener('popstate', () => setTimeout(refresh, 400));
})();
